/**
 * CONFIRM DIALOG — Dialog modale MUI per conferma azioni distruttive.
 *
 * Due livelli di attrito, scelti per-richiesta dalle `ConfirmDialogOptions`:
 * - DEFAULT: leggere e premere Conferma. Copre la stragrande maggioranza dei
 *   casi, dove il costo di un errore è rifare l'operazione.
 * - `requireTypedConfirmation`: il pulsante nasce SPENTO e si accende solo
 *   ridigitando una parola (ELIMINA / DELETE, o quella passata dal chiamante).
 *   Per ciò che non si può disfare. Il controllo è tutto qui, lato client: non
 *   è una misura di sicurezza — chi ha il permesso di eliminare lo ha comunque
 *   via API — ma un modo di trasformare un gesto automatico in uno deliberato.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button, { type ButtonProps } from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';

/** Opzioni per-richiesta della conferma (titolo, label e colore del pulsante).
 *  Sono lo stesso set che `useConfirmDialog().requestConfirm(msg, cb, options)`
 *  propaga al dialog: un'unica istanza condivisa serve così conferme sia
 *  distruttive (default) sia non-distruttive (es. `confirmColor: 'primary'`). */
export interface ConfirmDialogOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Colore del bottone di conferma. Default 'error' (azioni distruttive,
   *  es. eliminazioni). Usa 'primary' per conferme non distruttive. */
  confirmColor?: ButtonProps['color'];
  /** Se true il dialog chiede di RIDIGITARE una parola prima di lasciar
   *  confermare: il pulsante nasce spento e si accende solo a parola scritta.
   *  Per le azioni irreversibili di cui il solo clic su "Conferma" non è
   *  abbastanza — il gesto va compiuto due volte, e la seconda a mano.
   *  Default false: le conferme ordinarie restano un clic solo. */
  requireTypedConfirmation?: boolean;
  /** La parola da ridigitare. Default `confirm.typedWord` (ELIMINA / DELETE),
   *  che copre le eliminazioni; si passa esplicita quando l'azione non è
   *  un'eliminazione e la parola di default direbbe una cosa sbagliata.
   *  Ignorata senza `requireTypedConfirmation`. */
  typedConfirmationWord?: string;
}

interface ConfirmDialogProps extends ConfirmDialogOptions {
  open: boolean;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  confirmColor = 'error',
  requireTypedConfirmation = false,
  typedConfirmationWord,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');

  // `||` e non `??`: il nullish coalescing lascia passare la STRINGA VUOTA, e una parola vuota
  // renderebbe il confronto vero a campo vuoto — il pulsante nascerebbe già acceso con il campo
  // in bella vista, cioè la protezione disattivata senza che si veda. Un chiamante che passa una
  // parola vuota (una variabile non ancora risolta) ricade così sul default, che è l'unico
  // ripiego sensato per un valore che deve esistere per forza.
  const word = typedConfirmationWord || t('confirm.typedWord');

  // Il campo si svuota a ogni APERTURA, non alla chiusura. Il dialog è un'istanza sola e
  // condivisa — la stessa serve tutte le conferme della pagina — quindi resta montata fra una
  // richiesta e l'altra e con lei resterebbe il testo digitato: la seconda eliminazione si
  // aprirebbe con la parola già scritta e il pulsante già acceso, che è esattamente ciò che
  // questa funzionalità esiste per impedire. Pulire qui e non in onConfirm/onCancel copre anche
  // le chiusure che non passano di lì (Esc, clic sul backdrop).
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  // Confronto INSENSIBILE alle maiuscole e con i lati tagliati: la soglia è ricordare quale
  // parola scrivere, non azzeccarne la forma. Il trim copre l'incolla, che porta con sé spazi.
  const isConfirmEnabled = !requireTypedConfirmation
    || typed.trim().toUpperCase() === word.toUpperCase();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title ?? t('confirm.defaultTitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        {requireTypedConfirmation && (
          <TextField
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            // Invio = conferma, ma solo a parola giusta: un campo di testo dove l'Invio non fa
            // nulla si legge come rotto. La guardia ripete quella del pulsante invece di
            // affidarsi al suo `disabled`, che qui non intercetta niente — l'evento nasce sul
            // campo, non sul bottone.
            // preventDefault: chiudendosi, il Dialog rende il fuoco al pulsante che l'aveva
            // aperto (il cestino di una riga, «Elimina» della scheda), e l'azione predefinita
            // dell'Invio ancora in corso lo «cliccava», riaprendo la conferma a campo vuoto
            // dopo che la cancellazione era già partita.
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;

              e.preventDefault();

              if (isConfirmEnabled) onConfirm();
            }}
            label={t('confirm.typedLabel', { word })}
            // Il fuoco al campo e non al pulsante di conferma: è il campo che apre la strada,
            // e portarcelo evita che il primo gesto sia cercarlo col mouse.
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            // Il default del browser proporrebbe le parole già digitate altrove, e su un campo
            // la cui compilazione È la conferma un suggerimento a un carattere vanificherebbe
            // il passaggio.
            autoComplete="off"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">{cancelLabel ?? t('confirm.defaultCancel')}</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={confirmColor}
          disabled={!isConfirmEnabled}
        >
          {confirmLabel ?? t('confirm.defaultConfirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
